import React from 'react';
import Link from 'next/link';
import GeneralChart from './GeneralChart';
import { OptionType, SeoHotLinksType } from '@/types/common';
import { SimilarJobTitleType } from '@/types/job-title';
import { CityOption } from '@/types/city';
import { DEFAULT_SEO_HOT_LINKS } from '@/constants/common';
import {
  COMPANY_ROUTER_PATH,
  JOB_TITLE_PAGE_ROUTER_PATH,
} from '@/constants/router-path';

type LinksContainerProps = {
  seoHotLinks: SeoHotLinksType;
  similarJobTitles?: SimilarJobTitleType[];
  aliases?: OptionType[];
  title?: string;
  city?: Pick<CityOption, 'name' | 'pinyin'> | null;
};

const LinksContainer = ({
  title,
  similarJobTitles = [],
  aliases = [],
  seoHotLinks = DEFAULT_SEO_HOT_LINKS,
  city,
}: LinksContainerProps) => {
  const {
    hot_job_titles: hotJobs,
    hot_companies: hotCompanies,
    hot_cities: hotCities,
  } = seoHotLinks;

  const [activeTab, setActiveTab] = React.useState(
    aliases.length > 0 ? 0 : similarJobTitles.length > 0 ? 1 : 2,
  );

  const tabs = React.useMemo(
    () => [
      {
        label: '相似职位',
        data: similarJobTitles,
        baseName: '{1}',
        baseLink: city
          ? `/${city.pinyin}/{1}/`
          : `${JOB_TITLE_PAGE_ROUTER_PATH}{1}/`,
      },
      {
        label: '热门职位',
        data: hotJobs,
        baseName: '{1}招聘',
        baseLink: city
          ? `/${city.pinyin}/{1}/`
          : `${JOB_TITLE_PAGE_ROUTER_PATH}/{1}/`,
      },
      {
        label: '热招公司',
        data: hotCompanies,
        baseName: '{1}',
        baseLink: `${COMPANY_ROUTER_PATH}{1}/`,
      },
      {
        label: '热招城市',
        data: hotCities,
        baseName: '{1}招聘数据',
        baseLink: '/{1}/',
        isCity: true,
      },
      {
        label: '相似名称',
        data: aliases,
        baseName: '{1}',
        baseLink: city
          ? `/${city.pinyin}/{1}/`
          : `${JOB_TITLE_PAGE_ROUTER_PATH}{1}/`,
      },
    ],
    [aliases, similarJobTitles, hotJobs, hotCompanies, hotCities, city],
  );

  return (
    <GeneralChart title={title} margin="m-0">
      <div className="flex gap-10 mb-4">
        {tabs.map(({ label, data }, index) => {
          if (data.length === 0) {
            return null;
          }
          return (
            <span
              key={label}
              onClick={() => setActiveTab(index)}
              className={`pb-0.5 transition-colors cursor-pointer font-semibold ${
                activeTab === index
                  ? 'border-b border-primary border-solid'
                  : 'text-web-grey-600 hover:text-primary'
              }`}
            >
              {label}
            </span>
          );
        })}
      </div>
      {tabs.map(({ label, data, baseName, baseLink, isCity }, index) => (
        <div
          key={label}
          className={`flex flex-wrap gap-4 ${index === activeTab ? '' : 'hidden'}`}
        >
          {data.map((item, index) => {
            const name = baseName.replace(
              '{1}',
              isCity ? item.name.replace('市', '') : item.name,
            );
            return (
              <Link
                title={name}
                key={index + name}
                href={baseLink.replace(
                  '{1}',
                  'id' in item ? item.id : item.pinyin,
                )}
                className="text-sm text-black/85 hover:underline"
              >
                {name}
              </Link>
            );
          })}
        </div>
      ))}
    </GeneralChart>
  );
};

export default LinksContainer;
